import { Sigma, Brain, AppWindow } from "lucide-react";
import { SectionHead } from "@/components/SectionHead";
import { Reveal } from "@/components/Reveal";

const PILLARS = [
    { icon: Sigma, label: "Lean Six Sigma Process Engineering" },
    { icon: Brain, label: "Advanced Behavioral Insights" },
    { icon: AppWindow, label: "Digital Adoption Tools" },
];

export default function Solution() {
    return (
        <section id="solution" className="relative overflow-hidden py-24 sm:py-28" data-testid="section-solution-hitl-architecture">
            {/* industrial texture backdrop */}
            <img
                src="https://images.pexels.com/photos/2100918/pexels-photo-2100918.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.08]"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-ink via-ink/70 to-ink" />

            <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
                <SectionHead
                    index="02"
                    label="The Solution"
                    title={
                        <>
                            Human-in-the-Loop <span className="text-blaze">Architecture</span>
                        </>
                    }
                    light
                />

                <div className="mt-12 grid grid-cols-1 gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <p className="text-base leading-relaxed text-slate-300 sm:text-lg">
                                To scale successfully, you cannot treat process optimization and
                                human performance as separate initiatives.
                            </p>
                            <p className="mt-6 text-base leading-relaxed text-slate-300 sm:text-lg">
                                At HITL.partners, we build systems where automated processing and
                                human oversight work in perfect tandem. By combining Lean Six
                                Sigma process engineering with advanced behavioral insights and
                                digital adoption tools, our multidisciplinary toolkit ensures
                                your systems are mathematically precise and your people are
                                aligned to execute them.
                            </p>
                        </Reveal>

                        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
                            {PILLARS.map((p, i) => (
                                <Reveal key={p.label} delay={0.1 + i * 0.1} className="h-full">
                                    <div className="flex h-full items-center gap-4 border border-line bg-ink/70 p-5 backdrop-blur-sm transition-colors duration-300 hover:border-blaze/50">
                                        <p.icon className="h-6 w-6 shrink-0 text-signal" strokeWidth={1.75} />
                                        <span className="text-sm font-semibold leading-snug text-slate-200">
                                            {p.label}
                                        </span>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center lg:col-span-5">
                        <Reveal delay={0.2} className="w-full">
                            <div className="border border-line bg-panel/70 p-8 sm:p-10">
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                                    // The operating principle
                                </div>
                                <p className="mt-6 font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tight sm:text-5xl">
                                    <span className="text-white">Process</span>{" "}
                                    <span className="text-outline">Engineered.</span>
                                    <br />
                                    <span className="text-white">Human</span>{" "}
                                    <span className="text-blaze">Optimized.</span>
                                </p>
                                <div className="mt-8 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                    <span className="h-1.5 w-1.5 animate-blink bg-blaze" />
                                    Automated processing × human oversight
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}
